import { useMemo } from "react";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import { htmlDecode } from "@hopper-b2b/utilities";
import dayjs from "dayjs";
import type { CustomerReviewComponentProps } from "./CustomerReview";

export const useCustomerReview = ({ review }: CustomerReviewComponentProps) => {
  const { t, formatDateTime } = useI18nContext();

  const created = useMemo(() => {
    try {
      if (review.created == null) {
        return "";
      }

      const parsedDate = dayjs(review.created, "DD MMM YYYY");
      if (!parsedDate.isValid()) {
        console.warn("Invalid date format for review.created:", review.created);
        return "";
      }

      return formatDateTime(
        parsedDate.toDate(),
        DateTimeFormatStyle.MediumDate
      );
    } catch (error) {
      console.error("Error formatting review date:", error);
      return "";
    }
  }, [formatDateTime, review.created]);

  return useMemo(
    () => ({
      context: { review, rating: review.score * 5 },
      handlers: {},
      text: {
        title: `"${htmlDecode(review.title) ?? review.title}"`,
        reviewInfo: t("core-hotels.shop.customerReviews.reviewInfo", {
          name: review.author,
          date: created,
        }),
        content: review.text
          .split(/\r?\n/)
          .filter((line) => line.trim() !== "")
          .map((line) => htmlDecode(line) ?? line),
      },
    }),
    [created, review, t]
  );
};
